import { RewardDays } from "@lemmonhq/types";

/** Monday-first so week ranges read the way people expect. */
const DAY_ORDER = [
  "monday",
  "tuesday",
  "wednesday",
  "thursday",
  "friday",
  "saturday",
  "sunday",
];

const DAY_LABEL: Record<string, string> = {
  monday: "Mon",
  tuesday: "Tue",
  wednesday: "Wed",
  thursday: "Thu",
  friday: "Fri",
  saturday: "Sat",
  sunday: "Sun",
};

export type ScheduleText = {
  /** Short day summary, e.g. "Every day", "Mon–Fri", "Mon, Wed, Sat". */
  days: string;
  /** Shared time range across every active day, or null when they differ / run all day. */
  time: string | null;
  /** Long form for the cell tooltip. */
  title: string;
};

const isAllDay = (ranges: { start_time: string; end_time: string }[]) =>
  ranges.length === 1 &&
  ranges[0].start_time <= "00:00" &&
  ranges[0].end_time >= "23:59";

const formatRanges = (ranges: { start_time: string; end_time: string }[]) =>
  ranges.map((r) => `${r.start_time}–${r.end_time}`).join(", ");

/** Collapses runs of consecutive days into ranges: [Mon,Tue,Wed,Fri] → "Mon–Wed, Fri". */
const collapseDays = (activeDays: string[]) => {
  const indices = activeDays.map((d) => DAY_ORDER.indexOf(d));
  const parts: string[] = [];
  let runStart = 0;

  for (let i = 0; i < indices.length; i++) {
    const isLast = i === indices.length - 1;
    const breaksRun = isLast || indices[i + 1] !== indices[i] + 1;
    if (!breaksRun) continue;

    const runLength = i - runStart + 1;
    if (runLength >= 3) {
      parts.push(
        `${DAY_LABEL[activeDays[runStart]]}–${DAY_LABEL[activeDays[i]]}`,
      );
    } else {
      for (let j = runStart; j <= i; j++) parts.push(DAY_LABEL[activeDays[j]]);
    }
    runStart = i + 1;
  }

  return parts.join(", ");
};

/**
 * Turns a promotion's day map into a compact cell label. Returns null when the
 * promotion has no schedule at all (i.e. it runs any time).
 */
export function getScheduleText(days: RewardDays): ScheduleText | null {
  const activeDays = DAY_ORDER.filter((d) => days[d]?.length > 0);
  if (!activeDays.length) return null;

  const dayText =
    activeDays.length === DAY_ORDER.length
      ? "Every day"
      : collapseDays(activeDays);

  const rangeTexts = activeDays.map((d) => formatRanges(days[d]));
  const shared = rangeTexts.every((r) => r === rangeTexts[0]);
  const allDay = activeDays.every((d) => isAllDay(days[d]));
  const time = shared && !allDay ? rangeTexts[0] : null;

  const title = activeDays
    .map(
      (d) =>
        `${DAY_LABEL[d]}: ${isAllDay(days[d]) ? "All day" : formatRanges(days[d])}`,
    )
    .join(" · ");

  return { days: dayText, time, title };
}
